import { useRef, useCallback } from 'react';
import { Link } from 'react-router';
import {
  Container,
  Title,
  Text,
  Button,
  Group,
  SimpleGrid,
  Card,
  ThemeIcon,
  Stack,
  Box,
  Image,
} from '@mantine/core';
import {
  IconHeart,
  IconUsers,
  IconHome,
  IconStethoscope,
  IconChefHat,
  IconSunHigh,
  IconArrowRight,
  IconPlayerPlay,
} from '@tabler/icons-react';
import classes from './HomePage.module.css';

const features = [
  {
    icon: IconHeart,
    title: 'Compassionate Care',
    description: 'Our dedicated staff provides personalized attention and genuine care for every resident, treating each person like family.',
  },
  {
    icon: IconHome,
    title: 'Village-Like Environment',
    description: 'Comfortable, beautifully designed spaces that feel like home, promoting independence while ensuring safety.',
  },
  {
    icon: IconStethoscope,
    title: 'Healthcare Support',
    description: '24/7 nursing staff and medication management to meet all health needs with professional medical oversight.',
  },
  {
    icon: IconChefHat,
    title: 'Nutritious Dining',
    description: 'Chef-prepared meals using fresh, local ingredients, accommodating dietary needs and preferences.',
  },
  {
    icon: IconUsers,
    title: 'Social Activities',
    description: 'Engaging programs and events that foster friendships, creativity, and a vibrant community life.',
  },
  {
    icon: IconSunHigh,
    title: 'Beautiful Grounds',
    description: 'Scenic gardens and outdoor spaces perfect for relaxation, walks, and connecting with nature.',
  },
];

export function HomePage() {
  const videoRef = useRef<HTMLVideoElement>(null);

  const handleWatchVideo = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = false;
    video.controls = true;
    video.currentTime = 0;
    video.requestFullscreen();

    const onExit = () => {
      if (!document.fullscreenElement) {
        video.muted = true;
        video.controls = false;
        document.removeEventListener('fullscreenchange', onExit);
      }
    };
    document.addEventListener('fullscreenchange', onExit);
  }, []);

  return (
    <>
      {/* Hero Video */}
      <Box className={classes.hero}>
        <video
          ref={videoRef}
          className={classes.heroVideo}
          autoPlay
          muted
          loop
          playsInline
          poster="/HILLSIDE-HARMONY-NO-SIGNAGE.png"
        >
          <source src="/HillsideHarmony_ENG_LoRez.mp4" type="video/mp4" />
          <track
            kind="captions"
            src="/HillsideHarmony_ENG_LoRez.vtt"
            srcLang="en"
            label="English"
            default
          />
        </video>
        <Box className={classes.heroOverlay}>
          <Container size="xl">
            <Stack gap="lg" maw={900}>
              <Title className={classes.heroTitle} c="white">
                Where Home and Village Meet
              </Title>
              <Text size="xl" c="white" fw={600} className={classes.acceptingBanner}>
                Be part of our community of Hmong and East-Asian
                language speakers to share in cultural foods, activities and friendship
                that spans across borders and languages. Inquire now!
              </Text>
              <Group gap="md">
                <Button
                  component={Link}
                  to="/contact"
                  size="lg"
                  rightSection={<IconArrowRight size={18} />}
                >
                  Schedule a Tour
                </Button>
                <Button
                  component={Link}
                  to="/services"
                  size="lg"
                  variant="outline"
                  color="white"
                >
                  Our Services
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  color="white"
                  leftSection={<IconPlayerPlay size={18} />}
                  onClick={handleWatchVideo}
                  className={classes.watchButton}
                >
                  Watch & Listen To Our Story
                </Button>
              </Group>
            </Stack>
          </Container>
        </Box>
      </Box>

      {/* Features Section */}
      <Box py={80} bg="gray.0">
        <Container size="xl">
          <Stack align="center" gap="sm" mb={50}>
            <Text c="harmony.6" fw={600} tt="uppercase" size="sm">
              Why Choose Us
            </Text>
            <Title order={2} ta="center" maw={600}>
              Experience the Hillside Harmony Difference
            </Title>
            <Text c="dimmed" ta="center" maw={600}>
              We combine professional expertise with heartfelt compassion to create
              an environment where residents thrive and families find peace of mind.
            </Text>
          </Stack>

          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="xl">
            {features.map((feature) => (
              <Card key={feature.title} padding="xl" className={classes.featureCard}>
                <ThemeIcon
                  size={50}
                  radius="md"
                  variant="light"
                  color="harmony"
                  mb="md"
                >
                  <feature.icon size={26} stroke={1.5} />
                </ThemeIcon>
                <Text fw={600} size="lg" mb="xs">
                  {feature.title}
                </Text>
                <Text c="dimmed" size="sm">
                  {feature.description}
                </Text>
              </Card>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* About Preview Section */}
      <Box py={80}>
        <Container size="xl">
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing={50}>
            <Box>
              <Image
                src="/HILLSIDE-HARMONY-NO-SIGNAGE.png"
                alt="Hillside Harmony assisted living facility exterior"
                radius="lg"
                h={400}
                fit="cover"
              />
            </Box>
            <Stack justify="center" gap="lg">
              <Text c="harmony.6" fw={600} tt="uppercase" size="sm">
                About Hillside Harmony
              </Text>
              <Title order={2}>
                Continuing a 60-Year Legacy of Caring
              </Title>
              <Text c="dimmed">
                As a new facility, Hillside Harmony is honored to continue the 60-year legacy
                of compassionate care that has defined this building. Our mission is simple:
                to provide exceptional care that honors the dignity, independence, and
                individuality of each resident.
              </Text>
              <Text c="dimmed">
                Our team of dedicated caregivers, nurses, and staff members are not just
                employees—they're family. Together, we create a community where residents
                can enjoy life to the fullest while receiving the support they need.
              </Text>
              <Group>
                <Button
                  component={Link}
                  to="/about"
                  variant="outline"
                  rightSection={<IconArrowRight size={16} />}
                >
                  Learn More About Us
                </Button>
              </Group>
            </Stack>
          </SimpleGrid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box py={80} bg="harmony.7">
        <Container size="md">
          <Stack align="center" gap="lg">
            <ThemeIcon size={60} radius="xl" color="white" c="harmony.7">
              <IconHeart size={30} />
            </ThemeIcon>
            <Title order={2} c="white" ta="center">
              Ready to Find Your New Home?
            </Title>
            <Text c="white" ta="center" size="lg" maw={500} opacity={0.9}>
              We'd love to show you around and introduce you to our community.
              Schedule a personalized tour today.
            </Text>
            <Group gap="md">
              <Button
                component={Link}
                to="/contact"
                size="lg"
                color="white"
                c="harmony.7"
              >
                Schedule a Tour
              </Button>
              <Button
                component="a"
                href="tel:+16514000063"
                size="lg"
                variant="outline"
                color="white"
              >
                Call (651) 400-0063
              </Button>
            </Group>
          </Stack>
        </Container>
      </Box>
    </>
  );
}
